/**
 * Class defining event data descriptors to be used in
 * {@link Mojito.Services.SubscribeActions|SubscribeActions}.
 *
 * @see {@link Mojito.Services.SubscribeActions|SubscribeActions}
 *
 * @class EventsDataDescriptors
 * @name descriptors
 * @memberof Mojito.Services.SportsContent.Events
 */
export default class EventsDataDescriptors {
    /**
     * Factory method for creating an event data descriptor.
     *
     * @param {string} eventId - The event id.
     *
     * @returns {{dataType: string, eventId: *}} Event data descriptor.
     *
     * @function Mojito.Services.SportsContent.Events.descriptors#createEventDescriptor
     * @static
     */
    static createEventDescriptor(eventId) {
        return EventsDataDescriptors.createEventsDescriptor([eventId]);
    }

    /**
     * Factory method for creating multi event data descriptor.
     *
     * @param {Array<string>} eventIds - The list of event ids.
     *
     * @returns {{dataType: string, eventIds: Array<string>}} Events data descriptor.
     *
     * @function Mojito.Services.SportsContent.Events.descriptor#createEventsDescriptor
     * @static
     */
    static createEventsDescriptor(eventIds) {
        return {
            dataType: EventsDataDescriptors.DATA_TYPES.EVENT,
            eventIds,
        };
    }

    /**
     * Factory method for creating multi event data descriptor.
     *
     * @param {Array<string>} eventIds - The list of event ids.
     * @param {string} instanceId - Subscriber instance id.
     *
     * @returns {{dataType: string, requests: Array<object>}} Events data chunk descriptor.
     *
     * @function Mojito.Services.SportsContent.Events.descriptor#createEventsChunkDescriptor
     * @static
     */
    static createEventsChunkDescriptor(eventIds, instanceId) {
        return {
            dataType: EventsDataDescriptors.DATA_TYPES.EVENT_CHUNK,
            requests: [{ itemIds: eventIds, clientId: instanceId }],
        };
    }

    /**
     * Factory method for creating multi market data descriptor.
     *
     * @param {Array<string>} marketIds - The list of market ids.
     * @param {string} instanceId - Subscriber instance id.
     *
     * @returns {{dataType: string, requests: Array<object>}} Markets data chunk descriptor.
     *
     * @function Mojito.Services.SportsContent.Events.descriptor#createMarketsChunkDescriptor
     * @static
     */
    static createMarketsChunkDescriptor(marketIds, instanceId) {
        return {
            dataType: EventsDataDescriptors.DATA_TYPES.EVENT_MARKET_CHUNK,
            requests: [{ itemIds: marketIds, clientId: instanceId }],
        };
    }

    /**
     * Factory method for creating a event market data descriptor.
     *
     * @param {string} marketId - The market id.
     *
     * @returns {{dataType: string, marketId: *, eventId: *}} Event market data descriptor.
     *
     * @function Mojito.Services.SportsContent.Events.descriptor#createEventMarketDescriptor
     * @static
     */
    static createEventMarketDescriptor(marketId) {
        return EventsDataDescriptors.createEventMarketsDescriptor([marketId]);
    }

    /**
     * Factory method for creating multi market data descriptor.
     *
     * @param {Array<string>} marketIds - The list of market ids.
     *
     * @returns {{dataType: string, marketIds: Array<string>, eventId: string}} Event market data descriptor.
     *
     * @function Mojito.Services.SportsContent.Events.descriptors#createEventMarketDescriptor
     * @static
     */
    static createEventMarketsDescriptor(marketIds) {
        return {
            dataType: EventsDataDescriptors.DATA_TYPES.EVENT_MARKET,
            marketIds,
        };
    }

    /**
     * Factory method for creating an event tips data descriptor.
     *
     * @param {string} eventId - The event id.
     *
     * @returns {{dataType: string, eventId: *}} Event tip data descriptor.
     *
     * @function Mojito.Services.SportsContent.Events.descriptors#createEventTipDescriptor
     * @static
     */
    static createEventTipDescriptor(eventId) {
        return EventsDataDescriptors.createEventTipsDescriptor([eventId]);
    }

    /**
     * Factory method for creating multi event tips data descriptor.
     *
     * @param {Array<string>} eventIds - The list of event ids.
     *
     * @returns {{dataType: string, eventIds: Array<string>}} Event tips data descriptor.
     *
     * @function Mojito.Services.SportsContent.Events.descriptor#createEventTipsDescriptor
     * @static
     */
    static createEventTipsDescriptor(eventIds) {
        return {
            dataType: EventsDataDescriptors.DATA_TYPES.EVENT_TIPS,
            eventIds,
        };
    }
}

/**
 * System unique data descriptor types.
 *
 * @typedef DATA_TYPES
 * @property {string} EVENT - Event data descriptor type.
 * @property {string} EVENT_MARKET - Event market data descriptor type.
 * @property {string} EVENT_TIPS - Event tips data descriptor type.
 *
 * @memberof Mojito.Services.SportsContent.Events.descriptors
 */
EventsDataDescriptors.DATA_TYPES = {
    EVENT: 'event',
    EVENT_CHUNK: 'event-chunk',
    EVENT_MARKET: 'event-market',
    EVENT_MARKET_CHUNK: 'event-market-chunk',
    EVENT_TIPS: 'event-tips',
};
